import { Bot, Inbox, SearchX, Users, UserSearch } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { UsageExceededBanner } from '@/modules/billing/components/usageExceededBanner/UsageExceededBanner';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { useCandidateJobCountsQuery } from '@/modules/candidates/queries/useCandidateJobCountsQuery';
import type {
  CandidateJobApi,
  CandidateOriginApi,
} from '@/modules/candidates/types';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { JobPageHeader } from '@/modules/hiring-manager/components/JobPageHeader';
import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { PortalListSkeleton } from '@/modules/hiring-manager/components/PortalList';
import {
  activeStagesByFilter,
  type CandidateFilter,
  type CandidateStageQuery,
  useCandidatePipelineQuery,
} from '@/modules/hiring-manager/hooks/useCandidatePipelineQuery';
import {
  canRetryFailedSourcing,
  canUserRetryFailedSourcing,
} from '@/modules/hiring-manager/utils/jobSearchStatus';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import type { JobApi } from '@/modules/jobs/types';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import {
  automationFromJob,
  limitToPlan,
} from '@/pages/hm/job-automation/jobAutomation';

import { AgentBar } from './AgentBar';
import { stageOwner } from './agentSteps';
import { CandidateFilterBar } from './CandidateFilterBar';
import { ORIGIN_PARAM, parseOrigin, toOriginParam } from './candidateOrigin';
import { CandidateOriginFilter } from './CandidateOriginFilter';
import { CandidatePanel, type CandidatePanelPosition } from './CandidatePanel';
import { CandidateSearch } from './CandidateSearch';
import { stages } from './candidateStages';
import { CandidateTable, type CandidateTableGroup } from './CandidateTable';
import { ManualOutreachView } from './ManualOutreachView';
import { hasDraft, REVIEW_DRAFTS, REVIEW_PARAM } from './outreachDrafts';
import { type CareersPage, useCareersPage } from './useCareersPage';
import { useIsManualOutreach } from './useIsManualOutreach';

/** The candidate open in the side panel, so the panel survives a reload and can be linked. */
const CANDIDATE_PARAM = 'candidate';

type SubFilter = Exclude<CandidateFilter, 'all'>;

const candidateFilters: CandidateFilter[] = [
  'all',
  'inProgress',
  'waiting',
  'interviewing',
  'hired',
  'passed',
];

const emptyCopy: Record<SubFilter, string> = {
  inProgress: 'No one in progress right now.',
  waiting: 'Nothing waiting on you.',
  interviewing: 'No interviews scheduled yet.',
  hired: 'No one hired yet.',
  passed: 'You have not passed on anyone yet.',
};

// Links from before the redesign used the API status names.
const legacyFilters: Record<string, CandidateFilter> = {
  Submitted: 'waiting',
  Interviewing: 'interviewing',
  Hired: 'hired',
  Rejected: 'passed',
};

const parseFilter = (value: string | null): CandidateFilter => {
  if (value && candidateFilters.includes(value as CandidateFilter)) {
    return value as CandidateFilter;
  }
  return (value ? legacyFilters[value] : undefined) ?? 'all';
};

/**
 * The job's candidates as one table grouped by stage: the whole visible
 * pipeline on "All", one part of it per filter. A row opens in the side
 * panel; the full profile is a link away.
 */
export function HMJobCandidates() {
  const { jobId } = useParams<{ jobId: string }>();
  // Another job starts over: its own search, and stage lists that do not
  // keep the previous job's rows as placeholders while they load.
  return <JobCandidates jobId={jobId!} key={jobId} />;
}

function JobCandidates({ jobId }: { jobId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseFilter(searchParams.get('status'));
  const origin = parseOrigin(searchParams.get(ORIGIN_PARAM));
  const openCandidateId = searchParams.get(CANDIDATE_PARAM) ?? undefined;
  // Links keep the tenant and filter, not the open panel.
  const linkParams = new URLSearchParams(searchParams);
  linkParams.delete(CANDIDATE_PARAM);
  const searchSuffix = toSearchSuffix(linkParams);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useDebouncedValue(search, setSearch);
  const isSearching = search.trim() !== '';
  // Searching or picking a source shows part of the pipeline, not all of it.
  const isNarrowed = isSearching || origin !== undefined;

  const jobQuery = useJobQuery(jobId);
  const isManualOutreach = useIsManualOutreach(jobQuery.data);
  const workspacePlan = useWorkspacePlan();
  const { plan } = workspacePlan;
  const candidatesQuery = useCandidatePipelineQuery(
    jobId,
    filter,
    search,
    origin,
  );
  const countsQuery = useCandidateJobCountsQuery(jobId);
  const careersPage = useCareersPage(jobQuery.data);

  const updateFilter = (next: CandidateFilter) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'all') {
      nextParams.delete('status');
    } else {
      nextParams.set('status', next);
    }
    setSearchParams(nextParams);
  };

  const updateOrigin = (next: CandidateOriginApi | undefined) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) {
      nextParams.set(ORIGIN_PARAM, toOriginParam(next));
    } else {
      nextParams.delete(ORIGIN_PARAM);
    }
    setSearchParams(nextParams);
  };

  // On All, so a status filter cannot hide the people it announces.
  const showInbound = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(ORIGIN_PARAM, toOriginParam('Inbound'));
    nextParams.delete('status');
    setSearchParams(nextParams);
  };

  // Replace, not push: stepping through candidates should not fill Back.
  const setOpenCandidate = (candidateId: string | undefined) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (candidateId) next.set(CANDIDATE_PARAM, candidateId);
        else next.delete(CANDIDATE_PARAM);
        return next;
      },
      { replace: true },
    );

  const jobNotFound =
    jobQuery.error !== null &&
    'status' in jobQuery.error &&
    jobQuery.error.status === 404;

  if (jobQuery.isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-5">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-px w-full" />
        </div>
        <PortalListSkeleton rows={4} />
      </div>
    );
  }

  if (!jobQuery.data) {
    if (jobNotFound) {
      return (
        <div className="flex flex-col items-start gap-4">
          <p className="text-muted-foreground">Job not found.</p>
          <Button
            asChild
            className="h-8 rounded-md"
            size="sm"
            variant="outline"
          >
            <Link to={`/hm${searchSuffix}`}>Back to jobs</Link>
          </Button>
        </div>
      );
    }
    return (
      <HMLoadError
        description="We could not load this job. Check your connection and try again."
        isRetrying={jobQuery.isFetching}
        onRetry={() => void jobQuery.refetch()}
        title="Job unavailable"
      />
    );
  }

  const job = jobQuery.data;
  // Managed customers have no Automation tab: their recruiter decides who
  // handles what, so the page names no owners.
  const isSelfService =
    !workspacePlan.isLoading && !workspacePlan.error && plan !== 'managed';
  // The job's flags can ask for steps the plan does not include.
  const automation = limitToPlan(automationFromJob(job), workspacePlan);
  const progressStages = [
    candidatesQuery.stages.sourced,
    candidatesQuery.stages.shortlisted,
    candidatesQuery.stages.outreach,
    candidatesQuery.stages.prescreening,
  ];
  const stageGroups: CandidateTableGroup[] = stages
    .filter((stage) => activeStagesByFilter[filter].includes(stage.key))
    .map((stage) => ({
      ...stage,
      hint:
        isSelfService &&
        stage.key === 'prescreening' &&
        automation.prescreen === 'human'
          ? 'Booked or finished a screening call with you.'
          : stage.hint,
      owner: isSelfService ? stageOwner(stage.key, automation) : undefined,
      query: candidatesQuery.stages[stage.key],
    }));
  const activeStages = stageGroups.map((group) => group.query);
  const totalOf = (stageQueries: CandidateStageQuery[]) =>
    stageQueries.some((stage) => stage.totalCount === undefined)
      ? undefined
      : stageQueries.reduce(
          (total, stage) => total + (stage.totalCount ?? 0),
          0,
        );
  const allCount = totalOf(Object.values(candidatesQuery.stages));
  const inProgressCount = totalOf(progressStages);
  const focusedCounts = {
    waiting: candidatesQuery.stages.waiting.totalCount,
    interviewing: candidatesQuery.stages.interviewing.totalCount,
    hired: candidatesQuery.stages.hired.totalCount,
    passed: candidatesQuery.stages.passed.totalCount,
  };
  const counts = {
    all: allCount,
    inProgress: inProgressCount,
    ...focusedCounts,
  };
  const candidates = activeStages.some(
    (stage) => stage.candidates === undefined,
  )
    ? undefined
    : activeStages.flatMap((stage) => stage.candidates ?? []);
  const activeStageError = activeStages.some((stage) => stage.isError);
  const isFetchingActiveStages = activeStages.some((stage) => stage.isFetching);
  const refetchActiveStages = () => {
    activeStages
      .filter((stage) => stage.isError)
      .forEach((stage) => stage.refetch());
  };
  // A stage nobody is in only takes room; the table names what it left out.
  const isEmptyStage = (group: CandidateTableGroup) =>
    group.query.totalCount === 0 && !group.query.hasNextPage;
  const tableGroups = stageGroups.filter((group) => !isEmptyStage(group));
  // The job has nobody yet.
  const hasNoCandidates =
    filter === 'all' && !isNarrowed && candidates?.length === 0;
  // Reasons are the customer's to act on only when their own LinkedIn sends.
  // Managed customers have none: their recruiter sends from Finlay's.
  const showFailureReasons =
    automationFromJob(job).outreach === 'me' && plan !== 'managed';
  const tableProps = {
    showGroupHeaders: stageGroups.length > 1,
    showFailureReasons,
    // A stage without matches is not empty, so a search or source names none.
    emptyStageTitles: isNarrowed
      ? []
      : stageGroups.filter(isEmptyStage).map((group) => group.title),
    openCandidateId,
    onOpen: (candidateJob: CandidateJobApi) =>
      setOpenCandidate(candidateJob.candidate.id),
  };

  // Previous/Next walk the rows in table order, as far as they are loaded.
  const rows = tableGroups.flatMap((group) =>
    (group.query.candidates ?? []).map((candidateJob) => ({
      candidateJob,
      stage: group.key,
    })),
  );
  const openIndex = rows.findIndex(
    (row) => row.candidateJob.candidate.id === openCandidateId,
  );
  const position: CandidatePanelPosition | undefined =
    openIndex === -1
      ? undefined
      : {
          index: openIndex,
          total: rows.length,
          previousId: rows[openIndex - 1]?.candidateJob.candidate.id,
          nextId: rows[openIndex + 1]?.candidateJob.candidate.id,
        };

  const showsAgent =
    isSelfService &&
    job.sourcingState !== 'Draft' &&
    job.state !== 'Hired' &&
    job.state !== 'Closed';
  // Drafts live in Outreach, which only All and In progress load; that is
  // also where the review sheet is. Counted from the loaded rows, as the
  // sheet reviews them; a search or source would count only its matches.
  const drafts =
    isManualOutreach && !isNarrowed
      ? rows.filter((row) => hasDraft(row.candidateJob)).length
      : 0;
  // Only All always fetches the waiting stage; elsewhere the count would
  // depend on what an earlier visit cached. While narrowed it is matches.
  const decisions =
    isNarrowed || filter !== 'all'
      ? undefined
      : candidatesQuery.stages.waiting.totalCount;
  const openDrafts = () =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set(REVIEW_PARAM, REVIEW_DRAFTS);
        return next;
      },
      { replace: true },
    );

  const renderList = () => {
    if (candidates === undefined) {
      return <PortalListSkeleton rows={4} />;
    }
    if (hasNoCandidates) {
      return <NoCandidatesYet job={job} searchSuffix={searchSuffix} />;
    }
    const noMatches =
      isSearching && tableGroups.length === 0 ? (
        <PortalEmptyState
          action={{ label: 'Clear search', onClick: () => setSearchInput('') }}
          description="Check the spelling, or search by email."
          icon={SearchX}
          size="row"
          title={`No candidates match “${search.trim()}”.`}
        />
      ) : undefined;
    // On All only: a status filter keeps its own copy, as people from the
    // source may sit in other stages.
    if (
      origin &&
      !isSearching &&
      filter === 'all' &&
      tableGroups.length === 0
    ) {
      return (
        <NoOriginMatches
          careersPage={careersPage}
          onShowAll={() => updateOrigin(undefined)}
          origin={origin}
          workspaceHref={`/hm/workspace${searchSuffix}`}
        />
      );
    }
    if (!isSearching && filter !== 'all' && tableGroups.length === 0) {
      return <EmptyRow filter={filter} />;
    }
    // The customer does the outreach: the shortlist becomes selectable and
    // drafts get their review. Keyed by job: selection, drafting batch, and
    // edits are job-scoped. It stays mounted through a search without
    // matches, so the selection survives clearing it.
    if (isManualOutreach && (filter === 'all' || filter === 'inProgress')) {
      return (
        <ManualOutreachView
          groups={tableGroups}
          integrationsHref={`/hm/integrations${searchSuffix}`}
          isSearching={isSearching}
          job={job}
          key={job.id}
          noMatches={noMatches}
          table={tableProps}
        />
      );
    }
    if (noMatches) {
      return noMatches;
    }
    return <CandidateTable groups={tableGroups} {...tableProps} />;
  };

  return (
    <div className="flex flex-col gap-8">
      {jobQuery.isError && (
        <HMLoadError
          description="We could not refresh this job. Showing the previously loaded details."
          isRetrying={jobQuery.isFetching}
          onRetry={() => void jobQuery.refetch()}
          title="Job details may be out of date"
        />
      )}

      <JobPageHeader
        activeTab="candidates"
        // While narrowed, the stage counts are matches, not the pipeline.
        candidateCount={isNarrowed ? undefined : allCount}
        job={job}
        searchSuffix={searchSuffix}
      >
        {showsAgent && (
          <AgentBar
            automation={automation}
            decisions={decisions}
            drafts={drafts}
            integrationsHref={`/hm/integrations${searchSuffix}`}
            job={job}
            onReviewDecisions={() => updateFilter('waiting')}
            onReviewDrafts={openDrafts}
            careersPage={careersPage}
            newApplicants={countsQuery.data?.newApplicants}
            onShowInbound={origin === 'Inbound' ? undefined : showInbound}
          />
        )}
      </JobPageHeader>

      <UsageExceededBanner to={`/hm/billing${searchSuffix}`} />

      {/* One line when the content column fits it (narrowed by the
          candidate panel too, hence a container query); search and source
          drop below the stage filters otherwise. */}
      <div className="@container">
        <div className="flex flex-col gap-3 @3xl:flex-row @3xl:items-center @3xl:justify-between @3xl:gap-4">
          <CandidateFilterBar
            active={filter}
            counts={counts}
            onChange={updateFilter}
          />
          <div className="flex shrink-0 items-center gap-2">
            <CandidateSearch onChange={setSearchInput} value={searchInput} />
            <CandidateOriginFilter
              counts={countsQuery.data?.origin}
              onChange={updateOrigin}
              value={origin}
            />
          </div>
        </div>
      </div>

      {activeStageError && candidates !== undefined && (
        <HMLoadError
          description="We could not refresh the candidate list. Showing the previously loaded candidates."
          isRetrying={isFetchingActiveStages}
          onRetry={refetchActiveStages}
          title="Candidates may be out of date"
        />
      )}
      {activeStageError && candidates === undefined ? (
        <HMLoadError
          description="We could not load the candidates. Check your connection and try again."
          isRetrying={isFetchingActiveStages}
          onRetry={refetchActiveStages}
          title="Candidates unavailable"
        />
      ) : (
        renderList()
      )}

      {openCandidateId && (
        <CandidatePanel
          candidateId={openCandidateId}
          jobId={jobId}
          onClose={() => setOpenCandidate(undefined)}
          onNavigate={setOpenCandidate}
          position={position}
          row={openIndex === -1 ? undefined : rows[openIndex]}
          searchSuffix={searchSuffix}
          showFailureReason={showFailureReasons}
        />
      )}
    </div>
  );
}

function EmptyRow({ filter }: { filter: SubFilter }) {
  return <PortalEmptyState icon={Users} size="row" title={emptyCopy[filter]} />;
}

/** Nobody from the picked source; the way back is one click. */
function NoOriginMatches({
  origin,
  careersPage,
  workspaceHref,
  onShowAll,
}: {
  origin: CandidateOriginApi;
  careersPage: CareersPage | undefined;
  workspaceHref: string;
  onShowAll: () => void;
}) {
  const isInbound = origin === 'Inbound';
  return (
    <PortalEmptyState
      action={{ label: 'Show all sources', onClick: onShowAll }}
      description={
        isInbound && careersPage?.isEnabled === false ? (
          <>
            Turn on your careers page in{' '}
            <Link className="text-primary hover:underline" to={workspaceHref}>
              Workspace
            </Link>{' '}
            to receive applications.
          </>
        ) : undefined
      }
      icon={isInbound ? Inbox : Bot}
      size="row"
      title={
        isInbound
          ? 'No inbound candidates yet.'
          : 'No candidates sourced by Finlay yet.'
      }
    />
  );
}

/**
 * Why this job has nobody yet: a draft has not started, a paused search brings
 * nothing in, and a live one is simply still working.
 */
function NoCandidatesYet({
  job,
  searchSuffix,
}: {
  job: JobApi;
  searchSuffix: string;
}) {
  const { plan, isLoading, error } = useWorkspacePlan();
  const hasUserPermission = useCheckPermissions();
  const canEditJob = !isLoading && !error && plan !== 'managed';
  const canStartSourcing = hasUserPermission([permissions.Jobs.StartSourcing]);
  const reviewAction = canEditJob
    ? {
        label: 'Review job criteria',
        to: `/hm/jobs/${job.id}/edit${searchSuffix}`,
      }
    : undefined;

  if (job.sourcingState === 'Draft') {
    return (
      <PortalEmptyState
        action={
          canEditJob
            ? {
                label: 'Finish draft',
                to: `/hm/jobs/${job.id}/edit${searchSuffix}`,
              }
            : undefined
        }
        description={
          canEditJob
            ? 'Finish your draft, then start sourcing candidates.'
            : 'Your Finlay recruiter is setting up this job. Candidates will appear here for you to review.'
        }
        icon={UserSearch}
        title="Sourcing hasn’t started"
      />
    );
  }

  if (job.state === 'OnHold') {
    // No resume-sourcing mutation exists yet, so this state offers only the
    // review link, not a primary "resume" action.
    return (
      <PortalEmptyState
        description="Finlay is not finding new candidates. Existing candidates stay in the pipeline."
        icon={UserSearch}
        secondaryAction={reviewAction}
        title="Search paused"
      />
    );
  }

  if (job.sourcingState === 'Failed') {
    const jobCanRetry = canRetryFailedSourcing(job);
    const canRetrySourcing = canUserRetryFailedSourcing(job, {
      isSelfService: canEditJob,
      hasPermission: canStartSourcing,
    });
    const retryDescription = canRetrySourcing
      ? 'Finlay could not complete this search. Retry sourcing from the job header; the existing pipeline will be preserved.'
      : !jobCanRetry
        ? 'Finlay could not complete this search. This job is no longer active, so sourcing cannot be retried.'
        : isLoading
          ? 'Finlay could not complete this search. Checking whether sourcing can be restarted.'
          : error
            ? 'Finlay could not complete this search. Reload the page to confirm who can restart sourcing.'
            : plan === 'managed'
              ? 'Finlay could not complete this search. Your Finlay recruiter will restart sourcing.'
              : 'Finlay could not complete this search. Ask a workspace administrator to restart sourcing.';

    return (
      <PortalEmptyState
        description={retryDescription}
        icon={UserSearch}
        secondaryAction={reviewAction}
        title="Sourcing was interrupted"
      />
    );
  }

  if (job.sourcingState === 'Finished') {
    return (
      <PortalEmptyState
        description="Finlay completed this search. There are no candidates to review yet; any final results will appear here automatically."
        icon={UserSearch}
        secondaryAction={reviewAction}
        title="Search complete"
      />
    );
  }

  return (
    <PortalEmptyState
      description="Finlay is checking thousands of profiles. The first matches should appear in a few minutes."
      icon={UserSearch}
      live
      secondaryAction={reviewAction}
      title="Finding candidates"
    />
  );
}
